import { SelectOutlined } from '@amelio/ui-library';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { PenIcon } from '../PrepareMeetingIcons';

import type { PerformanceScoreLevel } from '@/types/competency/PerformanceScoreLevel';

import { useGetPerformanceScoreLevels } from '@/api/competency-services/performance-score-level';
import { useSelectedAppLanguage } from '@/hooks/company/useSelectedAppLanguage';
import { LevelIconPreview } from '@/pages/Competencies/components/levels/LevelIconPreview';
import { getTextByLanguage } from '@/utils/localization';

interface HandoutPerformanceScoreProps {
    performanceScore?: string;
    overallPerformanceLevelId?: string | null;
    overallPerformanceLevel?: PerformanceScoreLevel | null;
    onChange: (value: string) => void;
    disabled?: boolean;
}

export const HandoutPerformanceScore = ({
    overallPerformanceLevelId,
    overallPerformanceLevel,
    onChange,
    disabled = false,
}: HandoutPerformanceScoreProps) => {
    const { t } = useTranslation();
    const language = useSelectedAppLanguage();
    const [isEditing, setIsEditing] = useState(false);

    const { data: levels = [] } = useGetPerformanceScoreLevels(!disabled);

    // If disabled (completed), trust the provided snapshot object first so history stays stable.
    const selectedLevel =
        disabled && overallPerformanceLevel
            ? overallPerformanceLevel
            : levels.find((l) => l.id === overallPerformanceLevelId);

    const options = levels.map((opt) => ({
        value: opt.id as string,
        label: getTextByLanguage(opt.label, language),
        startIcon: (
            <div className={'flex h-5 w-5 items-center justify-center'}>
                <LevelIconPreview
                    icon={opt.icon}
                    iconColor={opt.color}
                    className={'h-4 w-4'}
                />
            </div>
        ),
    }));

    // No resolved level here means every competency was skipped (Not Observed) — the calculated score
    // deliberately does not fall back to a real level in that case (#15605). Never blank: tell the
    // manager why, in their own language, rather than showing an unexplained dash.
    const scoreLabel = selectedLevel
        ? getTextByLanguage(selectedLevel.label, language)
        : t('postReviewPrepareMeeting.performance.notObserved');

    const handleSelectChange = (value: string) => {
        if (disabled) {
            return;
        }
        onChange(value);
        setIsEditing(false);
    };

    return (
        <div className={'group mt-4 mb-3 grid grid-cols-12 gap-6 py-2 sm:mt-12 sm:mb-6 sm:py-4'}>
            <div
                className={
                    'border-purple/10 col-span-12 rounded-lg border bg-purple-50/60 p-4 md:col-span-5'
                }
            >
                <p
                    className={
                        'text-caption! mb-1 font-semibold tracking-wide text-gray-600 uppercase'
                    }
                >
                    {t('postReviewPrepareMeeting.performance.label')}
                </p>
                {isEditing ? (
                    <div className={'mt-2 max-w-xs'}>
                        <SelectOutlined
                            value={selectedLevel?.id ?? undefined}
                            onChange={handleSelectChange}
                            options={options}
                            placeholder={scoreLabel}
                            size={'sm'}
                            sort={'none'}
                            className={
                                '!border-secondary/10 !bg-secondary/5 !rounded-lg !py-5 !ring-0'
                            }
                            textClassName={'!text-violet text-body! !font-bold'}
                            chevronClassName={'!text-violet'}
                        />
                    </div>
                ) : (
                    <div className={'flex min-w-0 items-center gap-2'}>
                        {selectedLevel && (
                            <LevelIconPreview
                                icon={selectedLevel.icon}
                                iconColor={selectedLevel.color}
                                className={'h-5 w-5 flex-shrink-0'}
                            />
                        )}
                        <p
                            className={'text-violet text-body! min-w-0 font-bold break-words'}
                            style={selectedLevel ? { color: selectedLevel.color } : {}}
                        >
                            {scoreLabel}
                        </p>
                    </div>
                )}
            </div>
            {!disabled && (
                <div
                    className={
                        'col-span-12 flex items-center justify-start rounded-lg md:col-span-7'
                    }
                    data-pdf-hide={'true'}
                >
                    <button
                        className={
                            'bg-secondary/10 text-violet rounded-md p-2 opacity-100 transition-opacity sm:opacity-0 sm:group-hover:opacity-100'
                        }
                        type={'button'}
                        onClick={() => setIsEditing(true)}
                    >
                        <PenIcon className={'text-violet h-4 w-4'} />
                    </button>
                </div>
            )}
        </div>
    );
};
